import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, PageHeader, Spinner } from "@/components/shared";
import { AddVehicleWizard } from "@/components/AddVehicleWizard";
import FuelEditor from "@/components/FuelEditor";
import { cn } from "@/lib/utils";

const CAP_COLOR: Record<string, string> = { SIMULATED: "text-emerald-400", ONLINE: "text-emerald-400", AWAITING_FIRST_PACKET: "text-cyan-400", PENDING_HARDWARE_TEST: "text-amber-400", NOT_CONFIGURED: "text-slate-500", PROVIDER_ERROR: "text-rose-400" };

export function CapChip({ name, value }: { name: string; value: string }) {
  return <span data-testid={`cap-${name}`} className={cn("font-mono text-[10px] border border-[#26334d] rounded px-1.5 py-0.5", CAP_COLOR[value] || "text-slate-400")}>{name}: {value.replace(/_/g, " ")}</span>;
}

export default function Vehicles() {
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get("wizard") === "1");
  const qc = useQueryClient();
  const { can } = useAuth();
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState<any>({});
  // The IMEI lives on the device, not the vehicle, so it saves through its own endpoint.
  const [imei, setImei] = useState<string>("");
  const saveVeh = useMutation({
    mutationFn: async (v: any) => {
      if (Object.keys(edit).length) await api.patch(`/vehicles/${v.id}`, edit);
      const current = v.device?.uniqueId || "";
      const next = imei.trim();
      if (next !== current) {
        if (next) {
          const { data } = await api.put(`/vehicles/${v.id}/device`, { uniqueId: next });
          if (data.checksumValid === false) {
            toast.warning(`IMEI ${data.uniqueId} saved, but its check digit does not match — verify the number on the device.`);
          }
          return data;
        }
        await api.delete(`/vehicles/${v.id}/device`);
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vehicles"] }); setEditing(null); toast.success("Vehicle updated"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const { data, isLoading } = useQuery({ queryKey: ["vehicles"], queryFn: async () => (await api.get("/vehicles")).data, refetchInterval: 20000 });
  const del = useMutation({ mutationFn: (id: string) => api.delete(`/vehicles/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["vehicles"] }); toast.success("Vehicle removed"); }, onError: (e) => toast.error(errorMessage(e)) });
  const close = () => { setOpen(false); params.delete("wizard"); setParams(params); };

  return (
    <div data-testid="vehicles-page">
      <PageHeader title="Vehicles" subtitle={`${data?.length ?? 0} vehicles · the IMEI links a vehicle to its GPS/camera unit; fuel type enables cost and profitability`} actions={can("fleet:write") && <Button data-testid="open-add-vehicle-wizard-button" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-500 text-white"><Plus className="h-4 w-4 mr-1" /> Add Vehicle</Button>} />
      {isLoading ? <Spinner /> : !data?.length ? <EmptyState text="No vehicles yet. Use the wizard to add the first one." /> : (
        <div className="tg-card overflow-hidden">
          <Table data-testid="vehicles-table">
            <TableHeader><TableRow className="border-[#26334d] hover:bg-transparent"><TableHead className="tg-label">Plate</TableHead><TableHead className="tg-label">Vehicle</TableHead><TableHead className="tg-label">Driver on shift</TableHead><TableHead className="tg-label">Device</TableHead><TableHead className="tg-label">Fuel</TableHead><TableHead className="tg-label">Capabilities</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {data.map((v: any) => (
                <TableRow key={v.id} data-testid={`vehicle-row-${v.plate}`} className="border-[#26334d] hover:bg-[#1f2a3e]">
                  <TableCell className="font-mono font-semibold text-slate-100">
                    {editing === v.id
                      ? <input data-testid={`veh-plate-${v.id}`} defaultValue={v.plate} onChange={(e) => setEdit({ ...edit, plate: e.target.value })} className="w-24 h-7 rounded bg-[#0f141f] border border-[#26334d] px-2 text-xs" />
                      : v.plate}
                  </TableCell>
                  <TableCell className="text-slate-300 text-sm">
                    {editing === v.id ? (
                      <div className="flex flex-wrap gap-1">
                        {["make","model","year","color"].map((k) => (
                          <input key={k} data-testid={`veh-${k}-${v.id}`} placeholder={k} defaultValue={v[k] ?? ""} onChange={(e) => setEdit({ ...edit, [k]: k === "year" ? Number(e.target.value) : e.target.value })} className="w-16 h-7 rounded bg-[#0f141f] border border-[#26334d] px-2 text-xs" />
                        ))}
                      </div>
                    ) : <>{v.make} {v.model} <span className="text-slate-500">{v.year} · {v.color}</span></>}
                  </TableCell>
                  <TableCell className="text-sm">{v.currentDriver ? <span className="text-slate-200">{v.currentDriver.name}</span> : <span className="text-amber-400 font-mono text-xs">NO SHIFT</span>}</TableCell>
                  <TableCell className="text-xs">
                    {editing === v.id ? (
                      <div>
                        <input data-testid={`veh-imei-${v.id}`} value={imei} onChange={(e) => setImei(e.target.value)}
                               placeholder="IMEI (15 digits)" inputMode="numeric"
                               title="IMEI of the GPS/camera unit. Enables live video, remote capture and evidence clips. Leave empty to detach."
                               className="w-40 h-7 rounded bg-[#0f141f] border border-[#26334d] px-2 font-mono text-xs" />
                        <div className="text-slate-500 mt-1">{v.device ? `${v.device.provider} · currently ${v.device.uniqueId}` : "no device attached"}</div>
                      </div>
                    ) : v.device ? (
                      <div>
                        <div className="font-mono text-slate-200">{v.device.uniqueId}</div>
                        <div className="text-slate-500">{v.device.name} · {v.device.provider} · {v.device.lastSeen ? fmtAgo(v.device.lastSeen) : "no data"}</div>
                      </div>
                    ) : <span className="font-mono text-amber-400" title="Without an IMEI this vehicle has no live video, remote capture or evidence clips">NO IMEI</span>}
                  </TableCell>
                  <TableCell><FuelEditor vehicle={v} canEdit={can("fleet:write")} /></TableCell>
                  <TableCell><div className="flex flex-wrap gap-1">{Object.entries(v.capabilities || {}).map(([k, val]) => <CapChip key={k} name={k} value={val as string} />)}</div></TableCell>
                  <TableCell>
                    {can("fleet:write") && (editing === v.id ? (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" data-testid={`veh-save-${v.id}`} disabled={saveVeh.isPending} onClick={() => saveVeh.mutate(v)} className="text-emerald-400"><Check className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setEditing(null)} className="text-slate-500"><X className="h-4 w-4" /></Button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" data-testid={`veh-edit-${v.plate}`} onClick={() => { setEditing(v.id); setEdit({}); setImei(v.device?.uniqueId || ""); }} className="text-slate-500 hover:text-slate-200"><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" data-testid={`delete-vehicle-${v.plate}`} onClick={() => del.mutate(v.id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <AddVehicleWizard open={open} onClose={close} />
    </div>
  );
}
